import React from 'react';

import { DocumentTemplatesProvider } from '~/components/AiChat/hooks/useActionDocumentTemplate';
import { ActionPreview } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ActionPreview';
import {
  FULL_SCREEN_PREVIEW_CLASS_NAME,
  FullScreenToggleButton,
  useFullScreenToggle,
} from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/useFullScreenToggle';
import type { AiAction } from '~/components/AiChat/types';
import CardDialog from '~/components/CardDialog';
import FsIconButton from '~/components/core/FsWrappers/FsIconButton/FsIconButton';
import Text from '~/components/core/TextComponents/Text';
import EyeIcon from '~/components/icons/EyeIcon';

interface ActionPreviewDialogProps {
  action: AiAction;
  title: string;
  subtitle?: string;
  isDone: boolean;
  onClose: () => void;
}

const ActionPreviewDialog: React.FC<ActionPreviewDialogProps> = ({ action, title, subtitle, isDone, onClose }) => {
  const { isFullScreen, toggleFullScreen } = useFullScreenToggle();

  return (
    <CardDialog
      title={title}
      subheader={
        subtitle ? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {subtitle}
          </Text>
        ) : null
      }
      isDialog
      onClose={onClose}
      action={<FullScreenToggleButton isFullScreen={isFullScreen} onClick={toggleFullScreen} />}
      maxWidth="sm"
      fullWidth
      fullScreen={isFullScreen}
    >
      <div className="flex flex-col gap-16 pb-8">
        {/* Nothing links the action to what it filed, so a done action is rendered again, not fetched. */}
        {isDone ? (
          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            Rendered again from the claim&apos;s current data. The filed copy is in the claim&apos;s Documents tab.
          </Text>
        ) : null}
        <DocumentTemplatesProvider>
          <ActionPreview action={action} previewClassName={isFullScreen ? FULL_SCREEN_PREVIEW_CLASS_NAME : undefined} />
        </DocumentTemplatesProvider>
      </div>
    </CardDialog>
  );
};

/**
 * Preview, beside Execute while the action waits and alone once it is executed: the action's preview on
 * its own, to read before or after executing it. It executes nothing. Render it only where
 * `hasActionPreview` holds.
 */
export const ActionPreviewButton: React.FC<Omit<ActionPreviewDialogProps, 'onClose'> & { className?: string }> = ({
  className,
  ...dialogProps
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <FsIconButton
        icon={EyeIcon}
        tooltipText="Preview"
        aria-label="Preview"
        className={className}
        onClick={() => setIsOpen(true)}
        ignorePermissions
      />
      {isOpen ? <ActionPreviewDialog {...dialogProps} onClose={() => setIsOpen(false)} /> : null}
    </>
  );
};
